/**
 * AgentTile — 200px-wide agent card for the Agents room factory floor grid.
 *
 * Props:
 *   agent      — agent object (see shape below)
 *   onSelect   — (agent) => void — opens AgentDrawer
 *   isOverride — boolean — "I am driving" mode: dims non-acting platform agents
 *
 * No useState for time. Uses useEffect + setInterval(30 000) to keep
 * last-active label live. Computes initial value at mount time.
 */
import { useEffect, useRef } from "react";

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Format an ISO string as a compact relative label.
 * Returns "<1m" | "5m ago" | "2h ago" | "3d ago" | "—"
 */
function formatLastActive(isoString) {
  if (!isoString) return "—";
  const diffMs = Date.now() - new Date(isoString).getTime();
  if (diffMs < 0) return "<1m";
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return "<1m";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Format a nullable USD number as "$X.XX" or "—".
 */
function fmtUsd(val) {
  if (val == null) return "—";
  return `$${Number(val).toFixed(2)}`;
}

// ── Status config ──────────────────────────────────────────────────────────────

const STATUS_DOT_COLOR = {
  active:   "#17803D",
  degraded: "#E07A4E",
  paused:   "var(--color-slate-500)",
  killed:   "var(--color-critical)",
  unknown:  "var(--color-slate-600)",
};

const STATUS_LABEL = {
  active:   "ACTIVE",
  degraded: "DEGRADED",
  paused:   "PAUSED",
  killed:   "KILLED",
  unknown:  "UNKNOWN",
};

// ── Pod chip config ────────────────────────────────────────────────────────────

const POD_CHIP_STYLE = {
  supply: {
    bg:     "rgba(23,128,61,0.13)",
    border: "rgba(23,128,61,0.35)",
    color:  "var(--color-green)",
  },
  match: {
    bg:     "rgba(224,122,78,0.13)",
    border: "rgba(224,122,78,0.35)",
    color:  "#C45E28",
  },
  demand: {
    bg:     "rgba(59,130,246,0.13)",
    border: "rgba(59,130,246,0.35)",
    color:  "#3B82F6",
  },
  legal: {
    bg:     "rgba(139,92,246,0.15)",
    border: "rgba(139,92,246,0.40)",
    color:  "#8B5CF6",
  },
};

// ── Sparkline (inline SVG, 7 bars) ─────────────────────────────────────────────

function Sparkline({ data, status }) {
  if (!data || data.length === 0) return null;

  const W   = 56;
  const H   = 18;
  const n   = data.length;
  const gap = 2;
  // Each bar width, evenly distributed
  const barW = Math.max(1, Math.floor((W - gap * (n - 1)) / n));
  const max  = Math.max(...data, 1);

  let barColor;
  if (status === "degraded") {
    barColor = "#E07A4E";
  } else if (status === "paused" || status === "killed") {
    barColor = "var(--color-slate-600)";
  } else {
    // active | unknown | fallback
    barColor = "#17803D";
  }

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      aria-label="7-day activity sparkline"
      style={{ display: "block", flexShrink: 0 }}
    >
      {data.map((val, i) => {
        const barH = Math.max(2, Math.round((val / max) * H));
        const x    = i * (barW + gap);
        const y    = H - barH;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={barH}
            rx={1}
            fill={barColor}
            opacity={i === n - 1 ? 0.95 : 0.50}
          />
        );
      })}
    </svg>
  );
}

// ── AgentTile ─────────────────────────────────────────────────────────────────

export default function AgentTile({ agent, onSelect, isOverride }) {
  // ── Derived values ─────────────────────────────────────────────────────────

  const status = agent.status_computed || agent.status || "unknown";
  const isLegal = agent.kind === "legal";

  const lastActiveIso = agent.last_run_at || agent.last_invoked_at || null;

  // Ref for the DOM node that shows last-active text — updated imperatively
  const lastActiveRef = useRef(null);

  // ── Live last-active ticker ────────────────────────────────────────────────

  useEffect(() => {
    function update() {
      if (lastActiveRef.current) {
        lastActiveRef.current.textContent = formatLastActive(lastActiveIso);
      }
    }
    update(); // immediate on mount / agent change
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, [lastActiveIso]);

  // ── Override overlay logic ─────────────────────────────────────────────────

  // isManualHeld: paused because of a manual_override: reason
  const isManualHeld =
    status === "paused" &&
    typeof agent.paused_reason === "string" &&
    agent.paused_reason.startsWith("manual_override:");

  // Dim: override active AND not manually held AND is a platform agent AND not paused
  const isDimmed =
    isOverride &&
    !isManualHeld &&
    agent.kind === "platform" &&
    status !== "paused";

  // ── Event handlers ─────────────────────────────────────────────────────────

  function handleClick() {
    if (onSelect) onSelect(agent);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (onSelect) onSelect(agent);
    }
  }

  // ── Style fragments ────────────────────────────────────────────────────────

  const podStyle = POD_CHIP_STYLE[agent.pod] || POD_CHIP_STYLE.supply;
  const dotColor = STATUS_DOT_COLOR[status] || STATUS_DOT_COLOR.unknown;
  const statusLabel = STATUS_LABEL[status] || "UNKNOWN";

  // Status label color mirrors dot color
  const statusLabelColor = dotColor;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        className="mc-agent-tile"
        role="button"
        tabIndex={0}
        aria-label={`Agent ${agent.agent_id}, status ${statusLabel}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{
          position: "relative",
          width: 200,
          boxSizing: "border-box",
          background: "var(--mc-surface)",
          border: "1px solid var(--mc-border)",
          borderRadius: 6,
          padding: "8px 10px 8px 10px",
          cursor: "pointer",
          userSelect: "none",
          opacity: isDimmed ? 0.7 : 1,
          transition: "opacity 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease",
        }}
      >
        {/* Dimmed overlay (isOverride + platform + not paused) */}
        {isDimmed && (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 6,
              background: "rgba(220,38,38,0.07)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        )}

        {/* ── Header row ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
            marginBottom: 6,
            minHeight: 14,
          }}
        >
          {/* Status dot + label */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              minWidth: 0,
              flex: "1 1 0%",
              overflow: "hidden",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: dotColor,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 8,
                letterSpacing: "0.06em",
                color: statusLabelColor,
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {statusLabel}
            </span>
          </div>

          {/* Pod chip */}
          <span
            style={{
              display: "inline-block",
              padding: "1px 4px",
              border: `1px solid ${podStyle.border}`,
              borderRadius: 999,
              background: podStyle.bg,
              color: podStyle.color,
              fontSize: 8,
              fontFamily: "var(--font-mono)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {agent.pod || "—"}
          </span>
        </div>

        {/* ── Agent class + ID row ── */}
        <div style={{ marginBottom: 6 }}>
          {/* agent_class */}
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--mc-text)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}
          >
            {agent.agent_class || "—"}
          </div>

          {/* agent_id + legal icon */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 4,
              marginTop: 1,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                color: "var(--color-slate-500)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: "1 1 0%",
                minWidth: 0,
              }}
              title={agent.agent_id}
            >
              {agent.agent_id}
            </span>
            {isLegal && (
              <span
                aria-label="Legal agent"
                style={{
                  fontSize: 8,
                  color: "var(--color-slate-400)",
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                ⚖
              </span>
            )}
          </div>
        </div>

        {/* ── Sparkline ── */}
        {agent.sparkline_7d && agent.sparkline_7d.length > 0 && (
          <div style={{ marginBottom: 6, display: "flex", alignItems: "flex-end" }}>
            <Sparkline data={agent.sparkline_7d} status={status} />
          </div>
        )}

        {/* ── Cost + last active row ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--color-slate-400)",
              whiteSpace: "nowrap",
            }}
          >
            {fmtUsd(agent.cost_24h_usd)} / 24h
          </span>
          <span
            ref={lastActiveRef}
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--color-slate-500)",
              whiteSpace: "nowrap",
            }}
          >
            {/* populated imperatively by useEffect */}
          </span>
        </div>

        {/* ── Degraded warning badge (top-right corner) ── */}
        {status === "degraded" && (
          <span
            aria-label="Degraded"
            style={{
              position: "absolute",
              top: 5,
              right: 5,
              fontSize: 10,
              lineHeight: 1,
              color: "#E07A4E",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            ⚠
          </span>
        )}

        {/* ── "HELD" badge when manually held under override ── */}
        {isManualHeld && isOverride && (
          <span
            aria-label="Manually held by override"
            style={{
              position: "absolute",
              top: 5,
              right: 5,
              fontSize: 8,
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: "var(--color-slate-300, #CBD5E1)",
              background: "rgba(148,163,184,0.18)",
              border: "1px solid rgba(148,163,184,0.35)",
              borderRadius: 3,
              padding: "1px 4px",
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            ⏸ HELD
          </span>
        )}
      </div>

      {/* ── Scoped hover styles via <style> (no Tailwind on the card) ── */}
      <style>{`
        .mc-agent-tile:hover {
          border-color: rgba(23, 128, 61, 0.40) !important;
          box-shadow: 0 0 0 1px rgba(23, 128, 61, 0.12), 0 2px 8px rgba(0, 0, 0, 0.18);
          background-color: rgba(23, 128, 61, 0.03);
        }
        .mc-agent-tile:focus-visible {
          outline: none;
          border-color: rgba(23, 128, 61, 0.70) !important;
          box-shadow: 0 0 0 3px rgba(23, 128, 61, 0.22);
        }
      `}</style>
    </>
  );
}
